// BlogList.jsx - SEO-Optimized Blog Listing Page
"use client";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Search, Loader2 } from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import { generateBreadcrumbSchema } from "../utils/seo";
import { Button } from "./ui/button";
import api from "../lib/api";

export default function BlogList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch articles from babylovegrowth.ai API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const data = await api.blog.getAllArticles();
        setArticles(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        console.error("Error fetching blog articles:", err);
        setError("Failed to load blog articles. Please try again later.");
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  // Filter articles based on search
  const filteredArticles = articles.filter(article =>
    article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.keywords?.some(keyword => keyword.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Sort by created date (newest first)
  const sortedArticles = [...filteredArticles].sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at)
  );

  // Featured articles (first 2)
  const featuredArticles = sortedArticles.slice(0, 2);
  const recentArticles = sortedArticles.slice(2);

  // Breadcrumb schema
  const breadcrumbs = [
    { name: "Home", url: "https://bookatherapy.com" },
    { name: "Blog", url: "https://bookatherapy.com/blog" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Massage Therapy Blog - Tips, Guides & Expert Advice"
        description="Expert advice on massage therapy, wellness tips, pain management, and health guides from certified RMTs. Learn about different massage types and their benefits."
        keywords="massage therapy blog, massage tips, RMT advice, wellness blog, massage guides, health and wellness, pain management tips"
        canonical="https://bookatherapy.com/blog"
        ogType="website"
        schema={generateBreadcrumbSchema(breadcrumbs)}
      />

      <NavbarDemo />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#252F4F] via-[#1e2640] to-[#252F4F] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Massage Therapy <span className="text-[#98C454]">Blog</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Expert advice, wellness tips, and comprehensive guides from certified massage therapists
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#98C454]"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#98C454] mx-auto mb-4" />
            <p className="text-gray-600">Loading articles...</p>
          </div>
        </section>
      )}

      {/* Error State */}
      {error && !loading && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-red-800 mb-4">Error Loading Articles</h3>
              <p className="text-red-600 mb-6">{error}</p>
              <Button
                onClick={() => window.location.reload()}
                className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F]"
              >
                Try Again
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Articles */}
      {!loading && !error && featuredArticles.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#252F4F] mb-8">Featured Articles</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  onClick={() => navigate(`/blog/${article.id}`)}
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#98C454]"
                >
                  {(article.cover_image_url || article.hero_image_url) && (
                    <img
                      src={article.cover_image_url || article.hero_image_url}
                      alt={article.title}
                      className="w-full h-52 object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(article.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold text-[#252F4F] mb-3 group-hover:text-[#98C454] transition-colors">
                      {article.title}
                    </h3>

                    {article.keywords && article.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {article.keywords.slice(0, 3).map((keyword, idx) => (
                          <span
                            key={idx}
                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-4">
                      <span className="text-sm text-gray-500">
                        {article.languageCode?.toUpperCase() || 'EN'}
                      </span>
                      <Button
                        variant="ghost"
                        className="text-[#98C454] hover:text-[#87b043] group-hover:translate-x-2 transition-transform"
                      >
                        Read More <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Articles */}
      {!loading && !error && recentArticles.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#252F4F] mb-8">Recent Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  onClick={() => navigate(`/blog/${article.id}`)}
                  className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  {(article.cover_image_url || article.hero_image_url) && (
                    <img
                      src={article.cover_image_url || article.hero_image_url}
                      alt={article.title}
                      className="w-full h-44 object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#252F4F] mb-2 group-hover:text-[#98C454] transition-colors">
                      {article.title}
                    </h3>

                    {article.keywords && article.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {article.keywords.slice(0, 2).map((keyword, idx) => (
                          <span
                            key={idx}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(article.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* No Results */}
      {!loading && !error && filteredArticles.length === 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold text-[#252F4F] mb-4">No articles found</h3>
            <p className="text-gray-600 mb-6">Try searching with different keywords</p>
            <Button
              onClick={() => setSearchQuery("")}
              className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F]"
            >
              Clear Search
            </Button>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#252F4F] to-[#1e2640]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Experience Professional Massage Therapy?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Book your personalized massage session today
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/book")}
              className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-10 py-6 text-lg rounded-xl font-semibold"
            >
              Book Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
